/**
 * 15-Minute Fast Checkout Discount Timer
 * ----------------------------------------
 * Drop this file into: Online Store > Themes > Edit Code > Assets > discount-timer.js
 * Then include it in theme.liquid (see snippet at bottom of this file as a comment).
 *
 * HOW IT WORKS:
 * 1. On a user's first page load, we stamp localStorage with the visit start time.
 * 2. A countdown banner shows the time remaining to reach checkout in time.
 * 3. When the user clicks any "checkout" link/button, we check if they're still
 *    inside the 15-minute window:
 *      - If YES: redirect via /discount/<CODE> which auto-applies the discount,
 *        then forwards to /checkout.
 *      - If NO: send them to /checkout normally (no discount).
 *
 * IMPORTANT: The discount code below (DISCOUNT_CODE) must already exist in
 * Shopify Admin > Discounts as an active discount code. Create it once,
 * this script does not create discounts, it only decides whether to apply it.
 */

(function () {
  const STORAGE_KEY = "fastCheckoutStart";
  const WINDOW_MINUTES = 15;
  const WINDOW_MS = WINDOW_MINUTES * 60 * 1000;
  const DISCOUNT_CODE = "FAST5"; // <-- must match the code you create in Shopify Admin

  // 1. Set the start time on first visit (persists across pages in same browser)
  function getStartTime() {
    let start = localStorage.getItem(STORAGE_KEY);
    if (!start) {
      start = Date.now().toString();
      localStorage.setItem(STORAGE_KEY, start);
    }
    return parseInt(start, 10);
  }

  function getRemainingMs() {
    const start = getStartTime();
    const elapsed = Date.now() - start;
    return WINDOW_MS - elapsed;
  }

  function isEligible() {
    return getRemainingMs() > 0;
  }

  function removeBuyNowOverlays() {
    document.querySelectorAll(".fc-buynow-overlay").forEach((el) => el.remove());
  }

  // 2. Render a countdown banner (basic version — style via CSS as you like)
  function renderBanner() {
    if (document.getElementById("fast-checkout-banner")) return;

    const banner = document.createElement("div");
    banner.id = "fast-checkout-banner";
    banner.style.cssText =
    "position:relative;z-index:9999;background:#c98f8f;color:#fff;" +
    "text-align:center;padding:10px;font-size:14px;font-family:sans-serif;";
    document.body.prepend(banner);

    function tick() {
      const remaining = getRemainingMs();
      if (remaining <= 0) {
        banner.remove();
        removeBuyNowOverlays(); // let "Buy it now" behave normally again
        return;
      }
      const mins = Math.floor(remaining / 60000);
      const secs = Math.floor((remaining % 60000) / 1000)
        .toString()
        .padStart(2, "0");
      banner.textContent = `Complete Your Order in ${mins}:${secs} Minutes & Get an Extra 5% OFF`;
      requestAnimationFrame(() =>
        setTimeout(tick, 1000)
      );
    }
    tick();
  }

  // 3. Intercept checkout navigation to decide whether to apply the discount
  function interceptCheckoutLinks() {
    // Case A: plain <a href="/checkout"> links (some themes use these, e.g. mini-cart drawers)
    document.addEventListener("click", function (e) {
      const link = e.target.closest('a[href*="/checkout"]');
      if (link) {
        e.preventDefault();
        goToCheckout();
        return;
      }

      // Case B: <button name="checkout"> or <input name="checkout"> that is NOT
      // inside a <form> (rare, but some themes use JS-driven buttons like this)
      const looseButton = e.target.closest(
        'button[name="checkout"]:not(form button), input[name="checkout"]:not(form input)'
      );
      if (looseButton) {
        e.preventDefault();
        goToCheckout();
      }
    });

    // Case C: the standard Shopify cart form — <form action="/cart"> with a
    // <button type="submit" name="checkout">. This is what most themes use,
    // including the "CHECK OUT" button on your /cart page.
    document.addEventListener(
      "submit",
      function (e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;

        const isCartForm =
          form.action.includes("/cart") &&
          form.querySelector('button[name="checkout"], input[name="checkout"]');

        if (isCartForm) {
          e.preventDefault();
          goToCheckout();
        }
      },
      true // capture phase, so we intercept before Shopify's own handlers fire
    );
  }

  // 4. Intercept "Buy it now" / dynamic checkout buttons via overlay + modal
  //    (These render inside a secure cross-origin iframe, so we can't attach
  //    click listeners directly — instead we cover them with an invisible
  //    div that catches the click first.)
  function setupBuyNowOverlay() {
    if (!isEligible()) return; // offer expired — never intercept Buy it now again

    const containers = document.querySelectorAll(
      ".shopify-payment-button, [data-shopify='payment-button'], " +
      ".shopify-payment-button__button, .shopify-payment-button__button--unbranded"
    );

    containers.forEach((container) => {
      if (container.dataset.fastCheckoutOverlayed) return; // avoid double-wrapping
      container.dataset.fastCheckoutOverlayed = "true";

      container.style.position = "relative";

      const overlay = document.createElement("div");
      overlay.className = "fc-buynow-overlay";
      overlay.style.cssText =
        "position:absolute;top:0;left:0;right:0;bottom:0;z-index:10;cursor:pointer;";
      container.appendChild(overlay);

      overlay.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        openBuyNowModal(container);
      });
    });
  }

  function getFormContext(container) {
    const form = container.closest("form[action^='/cart/add']");
    if (!form) return null;
    const idInput = form.querySelector("input[name='id']");
    const qtyInput = form.querySelector("input[name='quantity']");
    return {
      variantId: idInput ? idInput.value : null,
      quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1,
    };
  }

  function addToCartThenGo(variantId, quantity, applyDiscount) {
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    })
      .then(() => {
        if (applyDiscount) {
          window.location.href = `/discount/${DISCOUNT_CODE}?redirect=/checkout`;
        } else {
          window.location.href = "/checkout";
        }
      })
      .catch(() => {
        // fallback: still send them to checkout even if the AJAX call hiccups
        window.location.href = "/checkout";
      });
  }

  function openBuyNowModal(container) {
    const ctx = getFormContext(container);
    if (!ctx || !ctx.variantId) {
      // Couldn't find variant info — fail safe, go to normal checkout
      window.location.href = "/checkout";
      return;
    }

    const eligible = isEligible();
    if (!eligible) {
      // Safety net: if this ever fires after expiry (e.g. a stray click
      // right at the boundary), just proceed straight to checkout, no modal.
      addToCartThenGo(ctx.variantId, ctx.quantity, false);
      return;
    }

    const remaining = getRemainingMs();
    const mins = Math.max(0, Math.floor(remaining / 60000));
    const secs = Math.max(0, Math.floor((remaining % 60000) / 1000))
      .toString()
      .padStart(2, "0");

    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:99999;" +
      "display:flex;align-items:center;justify-content:center;font-family:sans-serif;";

    const modal = document.createElement("div");
    modal.style.cssText =
      "background:#fff;padding:28px;border-radius:8px;max-width:360px;width:90%;" +
      "text-align:center;box-shadow:0 8px 30px rgba(0,0,0,0.2);";

    modal.innerHTML = `
      <h3 style="margin:0 0 10px;font-size:18px;">YOU STILL HAVE ${mins}:${secs}!</h3>
      <p style="margin:0 0 20px;font-size:14px;color:#444;">
        Complete your purchase now and save an extra <strong>5%</strong>.
      </p>
      <button id="fc-apply" style="width:100%;padding:12px;margin-bottom:10px;
        background:#c98f8f;color:#fff;border:none;border-radius:4px;cursor:pointer;
        font-size:14px;">Claim 5% OFF & Check Out</button>
      <button id="fc-skip" style="width:100%;padding:12px;background:transparent;
        color:#666;border:1px solid #ccc;border-radius:4px;cursor:pointer;font-size:14px;">
        Continue Shopping</button>
    `;

   if (!document.getElementById("fc-spin-style")) {
      const style = document.createElement("style");
      style.id = "fc-spin-style";
      style.textContent = "@keyframes fc-spin { to { transform: rotate(360deg); } }";
      document.head.appendChild(style);
    }

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) overlay.remove(); // click outside closes modal
    });

    const applyBtn = modal.querySelector("#fc-apply");
    const skipBtn = modal.querySelector("#fc-skip");

    if (applyBtn) {
      applyBtn.addEventListener("click", function () {
        applyBtn.disabled = true;
        applyBtn.style.cursor = "not-allowed";
        applyBtn.style.opacity = "0.8";
        applyBtn.innerHTML = `
          <span style="display:inline-flex;align-items:center;gap:8px;">
            <span style="width:14px;height:14px;border:2px solid #fff;
              border-top-color:transparent;border-radius:50%;
              display:inline-block;animation:fc-spin 0.7s linear infinite;">
            </span>
            Unlocking your discount...
          </span>
        `;
        skipBtn.disabled = true;
        skipBtn.style.opacity = "0.5";
        addToCartThenGo(ctx.variantId, ctx.quantity, true);
      });
    }
    skipBtn.addEventListener("click", function () {
      overlay.remove();
    });
  }

  function goToCheckout() {
    if (isEligible()) {
      // Applies the discount code, then forwards to checkout
      window.location.href = `/discount/${DISCOUNT_CODE}?redirect=/checkout`;
    } else {
      window.location.href = "/checkout";
    }
  }

  // Expose globally in case you want to trigger it from your own "Checkout" button
  window.fastCheckoutRedirect = goToCheckout;

  document.addEventListener("DOMContentLoaded", function () {
    getStartTime(); // ensure timer is started as early as possible
    renderBanner();
    interceptCheckoutLinks();
    setupBuyNowOverlay();

    // Shopify re-renders the Buy it now button when the customer changes
    // color/size (variant), so keep watching for it and re-overlay.
    const observer = new MutationObserver(function () {
      setupBuyNowOverlay();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();

/* ============================================================
   ADD THIS to theme.liquid, just before </body>:

   <script src="{{ 'discount-timer.js' | asset_url }}" defer></script>

   ============================================================ */